import { useCurrentUser } from './useCurrentUser'

/**
 * The crew feature values, keyed exactly as v1/legacy consumers expect —
 * sourced from `Organization.config` (crew.*, modules.timeclocks,
 * localization.useCubicMeters) rather than the deprecated
 * `appFeatures.crew` shim. Beta flags come from the sparse `me.flags` bag.
 */
export type CrewFeatures = {
  assignmentHideDetailsTillAcknowledgement: boolean
  assignmentReportAtDuration: number
  orderAllowOperatorToCancel: boolean
  timeclocksEnabled: boolean
  workTicketRequireSignature: boolean
  localeUseCubicMeters: boolean
  orderPhotosEnabled: boolean
  pushNotificationsEnabled: boolean
}

/**
 * Canonical defaults — mirror the server registry seeds. Applied only while
 * the user query is loading (the config store is dense: every value is
 * explicit once loaded).
 */
export const DEFAULT_FEATURES: CrewFeatures = {
  assignmentHideDetailsTillAcknowledgement: false,
  assignmentReportAtDuration: 1800,
  orderAllowOperatorToCancel: true,
  timeclocksEnabled: true,
  workTicketRequireSignature: false,
  localeUseCubicMeters: false,
  orderPhotosEnabled: false,
  pushNotificationsEnabled: false,
}

/**
 * All crew feature values, merged over the canonical defaults.
 * `loading` reflects the underlying current-user query.
 */
export const useFeatures = (): { features: CrewFeatures; loading: boolean } => {
  const { user, loading } = useCurrentUser()
  const config = user?.organization?.config
  // me.flags is an untyped Json bag — read defensively
  const flags = (user?.flags ?? {}) as Record<string, unknown>
  const features: CrewFeatures = config
    ? {
        assignmentHideDetailsTillAcknowledgement:
          config.crew.assignmentHideDetailsTillAck,
        assignmentReportAtDuration: config.crew.assignmentReportAtDuration,
        orderAllowOperatorToCancel: config.crew.orderAllowOperatorToCancel,
        timeclocksEnabled: config.modules.timeclocks,
        workTicketRequireSignature: config.crew.workTicketRequireSignature,
        localeUseCubicMeters: config.localization.useCubicMeters,
        orderPhotosEnabled: flags['order.photos'] === true,
        pushNotificationsEnabled: flags['notification.push'] === true,
      }
    : DEFAULT_FEATURES
  return { features, loading }
}

/** A single crew feature value, merged over the canonical default. */
export const useFeature = <K extends keyof CrewFeatures>(
  name: K,
): CrewFeatures[K] => useFeatures().features[name]
